import type { RequestTopic } from '@/lib/app-store'

export interface Chapter {
    id: string
    index: string
    label: string
}

export const chapters: Array<Chapter> = [
    { id: 'hero', index: '01', label: 'Overview' },
    { id: 'gear', index: '02', label: 'Gear' },
    { id: 'numbers', index: '03', label: 'Numbers' },
    { id: 'solutions', index: '04', label: 'Solutions' },
    { id: 'capabilities', index: '05', label: 'Capabilities' },
    { id: 'terrain', index: '06', label: 'Terrain' }
]

/** Section ids where the circular counter ticks to the next number (1 to 6). */
export const counterStarts: Array<string> = ['hero', 'gear', 'numbers', 'solutions', 'capabilities', 'software']

export const hero = {
    lead: 'Self-flying LiDAR for the structures we rely on.',
    follow: 'Every bridge, line and turbine, measured to the centimetre.'
}

export const promise = {
    label: 'Purpose',
    statement: 'Finding the hairline crack and the slow sag years before either becomes a headline.'
}

export const gear = {
    title: 'Gear',
    label: 'Sensor head',
    paragraphs: [
        'One sensor head, built in-house for nothing but measuring.',
        'It fires up to 240,000 laser pulses a second and catches five echoes from each, so leaves, wires and steel all land in the same point cloud.'
    ],
    annotations: ['905 nm eye-safe laser', 'Inertial unit at 200 Hz', '20 MP colour camera']
}

export interface Stat {
    value: number
    prefix: string
    unit: string
    label: string
    dial: string
}

export const stats: Array<Stat> = [
    { value: 5, prefix: '', unit: 'echoes', label: 'Echoes per pulse', dial: 'Echoes' },
    { value: 2, prefix: '±', unit: 'cm', label: 'Positional accuracy', dial: 'Accuracy' },
    { value: 450, prefix: '', unit: 'm', label: 'Usable range', dial: 'Reach' }
]

export type LandscapeKind = 'energy' | 'infrastructure' | 'industry' | 'geospatial'

export interface Solution {
    id: LandscapeKind
    label: string
    lead: string
    follow: string
    chips: Array<string>
    activeChip: number
    caption: string
}

export const solutions: Array<Solution> = [
    {
        id: 'energy',
        label: 'Energy',
        lead: 'Solar fields, wind farms and transmission corridors stretch further than any crew can walk in a season.',
        follow: 'One autonomous pass maps every row, blade and span, and flags whatever has moved since the last flight.',
        chips: ['Wind Turbines', 'Solar Farms', 'Power Lines', 'Substations'],
        activeChip: 1,
        caption: 'Solar field 42 ha, row alignment check'
    },
    {
        id: 'infrastructure',
        label: 'Infrastructure',
        lead: 'Bridges, viaducts and towers carry their loads every day, often over water, rail or open road.',
        follow: 'We measure deflection and surface wear to the millimetre while the traffic keeps moving underneath.',
        chips: ['Bridges', 'Towers', 'Rail Corridors', 'Tunnels'],
        activeChip: 0,
        caption: 'Truss bridge 310 m, deflection check'
    },
    {
        id: 'industry',
        label: 'Industry',
        lead: 'Plants, tank farms and stockyards change shape every shift and rarely stop for an audit.',
        follow: 'Scheduled flights record tanks, racks and piles in a single sweep, so inspection becomes routine instead of a shutdown.',
        chips: ['Refineries', 'Storage Tanks', 'Pipe Racks', 'Stockpiles'],
        activeChip: 1,
        caption: 'Process plant 1.8 km², asset survey'
    },
    {
        id: 'geospatial',
        label: 'Geospatial',
        lead: 'Slopes, coastlines and forests move quietly between survey seasons.',
        follow: 'Multi-echo pulses see through the canopy to bare ground, giving you a fresh elevation model in a day.',
        chips: ['Topography', 'Forestry', 'Coastlines', 'Mining'],
        activeChip: 0,
        caption: 'Alpine catchment 96 km², bare-ground model'
    }
]

export interface Spec {
    label: string
    value: number
    prefix?: string
    suffix?: string
    unit?: string
}

export const specs: Array<Spec> = [
    { label: 'Scope', value: 360, suffix: '°' },
    { label: 'Scan Modes', value: 2 },
    { label: 'Coverage', value: 100, unit: 'km²/day' },
    { label: 'Max Altitude', value: 500, unit: 'm' },
    { label: 'Accuracy', value: 2, prefix: '±', unit: 'cm' },
    { label: 'Point Rate (K/S)', value: 240 }
]

export interface Capability {
    id: 'scan' | 'connection' | 'compactness'
    label: string
    value: string
    description: string
}

export const capabilities: Array<Capability> = [
    {
        id: 'scan',
        label: 'Scan',
        value: '360°',
        description: 'Everything around and below is measured in one sweep at 240,000 points a second: ground, steel and canopy alike.'
    },
    {
        id: 'connection',
        label: 'Connection',
        value: '7 km',
        description: 'A secure two-band link streams the live point cloud back to the operator and steps around interference on its own.'
    },
    {
        id: 'compactness',
        label: 'Compactness',
        value: '42 cm',
        description: 'Folds to 42 cm and is ready to fly in minutes, with no ground station and no tools.'
    }
]

export const software = {
    title: 'Software',
    label: 'Pipeline',
    paragraph: 'The moment the drone lands, its data is cleaned, classified and packaged on its own, so a finished deliverable reaches your team in under 20 minutes.'
}

export interface TerrainMarker {
    id: string
    name: string
    readings: Array<[string, string]>
}

export const terrainMarkers: Array<TerrainMarker> = [
    { id: 'n07', name: 'Summit N-07', readings: [['Height', '2,891 m'], ['Gradient', '36.8°'], ['Open fracture', '2.3 mm']] },
    { id: 'r12', name: 'Crest R-12', readings: [['Height', '2,648 m'], ['Gradient', '29.5°'], ['Ground drift', '3.6 mm/yr']] },
    { id: 's03', name: 'Col S-03', readings: [['Height', '2,402 m'], ['Gradient', '18.2°'], ['Loose rock', 'Stable']] },
    { id: 'e21', name: 'Buttress E-21', readings: [['Height', '2,733 m'], ['Gradient', '47.1°'], ['Open fracture', '4.0 mm']] }
]

export interface FooterLink {
    label: string
    href?: string
    topic?: RequestTopic
}

export const footerColumns: Array<{ title: string, links: Array<FooterLink> }> = [
    {
        title: 'Platform',
        links: [
            { label: 'Payload', href: '#gear' },
            { label: 'Software', href: '#software' },
            { label: 'Integrations', href: '#software' },
            { label: 'Data Delivery', href: '#terrain' }
        ]
    },
    {
        title: 'Company',
        links: [
            { label: 'About', href: '#promise' },
            { label: 'Careers', topic: 'careers' },
            { label: 'Contact', topic: 'survey' },
            { label: 'Press', topic: 'press' }
        ]
    }
]

export const controller = {
    title: 'Controller',
    label: 'Pilot mode',
    statement: 'Take the controls. Fly it yourself.',
    follow: 'Every move you make is mapped in real time.',
    hints: [
        'Left stick — altitude + turn',
        'Right stick — pitch + roll',
        'Triggers — speed',
        'Buttons — lights · scan · wake'
    ],
    keys: 'WASD fly · arrows pitch · Q E triggers · R reset',
    scroll: 'Scroll to continue'
}

export const footer = {
    lead: 'What should we measure for you next?',
    follow: 'Book a survey flight and get a complete point cloud back within 24 hours.',
    cta: 'Book a Survey Flight',
    drag: 'Drag to turn',
    dragLabel: 'Drone viewer. Drag, or use the left and right arrow keys, to turn the drone.'
}

export const requestTopics: Array<{ value: RequestTopic, label: string }> = [
    { value: 'survey', label: 'Survey flight' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'careers', label: 'Careers' },
    { value: 'press', label: 'Press' }
]

export const sectors: Array<string> = ['Energy', 'Infrastructure', 'Industry', 'Geospatial', 'Other']

/** Scroll windows (in pinned progress) where each stepped section moves to its next state. */
export const stepWindows = {
    numbers: [[0.24, 0.4], [0.6, 0.76]] as Array<[number, number]>,
    solutions: [[0.2, 0.3], [0.45, 0.55], [0.7, 0.8]] as Array<[number, number]>,
    capabilities: [[0.22, 0.38], [0.62, 0.78]] as Array<[number, number]>
}
